import React from 'react';
import { Header, Footer, SEOHead } from '../components';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';

const PrivacyPolicyPage: React.FC = () => {
  useSEO(pageConfigs.privacyPolicy);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.privacyPolicy} />
      <Header />

      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <h1 className="text-4xl lg:text-5xl font-display font-bold text-slate-900 mb-6">
              Privacy Policy
            </h1>
            <p className="text-lg text-slate-600">
              Last updated: November 2024
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-lg max-w-none text-slate-600 space-y-8">
            
            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                1. Introduction
              </h2>
              <p>
                3 Arrows Media ("we," "us," "our," or "Company") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                2. Information We Collect
              </h2>
              <p>We collect information from you in several ways:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li><strong>Contact Information:</strong> Name, email address, phone number, and company name when you contact us</li>
                <li><strong>Website Usage Data:</strong> IP address, browser type, pages visited, and time spent on our site</li>
                <li><strong>Cookies and Tracking:</strong> We use cookies to improve your experience and analyze site usage</li>
                <li><strong>Form Submissions:</strong> Information you provide through contact forms, quote requests, or consultation bookings</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                3. How We Use Your Information
              </h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Provide and improve our services</li>
                <li>Respond to your inquiries and requests</li>
                <li>Send marketing communications (with your consent)</li>
                <li>Analyze and understand user behavior</li>
                <li>Comply with legal obligations</li>
                <li>Prevent fraudulent activities</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                4. Information Sharing and Disclosure
              </h2>
              <p>
                We do not sell, trade, or rent your personal information. We may share information with:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Service providers who assist in operating our website and conducting business</li>
                <li>Law enforcement when required by law</li>
                <li>Our business partners (only with your consent)</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                5. Data Security
              </h2>
              <p>
                We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                6. Your Privacy Rights
              </h2>
              <p>
                Depending on your location, you may have rights to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Opt-out of marketing communications</li>
                <li>Data portability</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at hello@3arrowsmedia.com
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                7. Cookies and Tracking Technologies
              </h2>
              <p>
                We use cookies to enhance your experience. You can control cookie settings in your browser preferences. Please note that disabling cookies may affect the functionality of our website.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                8. Third-Party Links
              </h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible for their privacy practices. We encourage you to review their privacy policies before providing any information.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                9. Changes to This Privacy Policy
              </h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the updated policy on our website and updating the "Last Updated" date.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                10. Contact Us
              </h2>
              <p>
                If you have questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="mt-4 bg-slate-50 p-6 rounded-lg">
                <p><strong>3 Arrows Media</strong></p>
                <p>Email: hello@3arrowsmedia.com</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
